import { create } from 'zustand';
import { StoreApi, UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { createFileLogger } from '../../../utils/logger';
import { API_BASE_URL } from '../../../api/config';
import fetchService from '../../../services/fetchService';
import { ContactEntity, ContactSkeleton, ContactTier, normalizeTier } from '../types/entity';
import { ContactsAppState, ContactsFilter, ContactsView } from '../types/state';

const log = createFileLogger('CONTACTS_APP');

const TIER_ORDER = ['close', 'regular', 'archived'];

interface ContactsAppStore extends ContactsAppState {
  initialize: (windowId: string, spaceId: string) => Promise<void>;
  cleanup: () => void;

  checkEmailStatus: () => Promise<void>;
  ensureAutomation: () => Promise<void>;

  loadContacts: () => Promise<void>;

  // Navigation
  setView: (view: ContactsView) => void;
  selectContact: (id: string | null) => void;
  setFilter: (filter: Partial<ContactsFilter>) => void;

  // CRUD
  createContact: (data: {
    name: string;
    email?: string;
    tier?: ContactTier;
    company?: string;
    phone?: string;
    notes?: string;
  }) => Promise<ContactEntity | null>;
  updateContact: (id: string, updates: Partial<ContactSkeleton> & { name?: string }) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;

  // Enrichment
  enrichContact: (id: string) => Promise<void>;
  discoverContacts: () => Promise<{ discovered: number; enriched: number }>;
  importFromCsv: (file: File, source?: string) => Promise<{ created: number; updated: number; skipped: number }>;

  // Form
  openForm: (contactId?: string) => void;
  closeForm: () => void;

  // Sync modal
  clearSyncLog: () => void;
  fetchSourcesStatus: () => Promise<void>;

  // Computed
  getFilteredContacts: () => ContactEntity[];
}

type ContactsStoreHook = UseBoundStore<StoreApi<ContactsAppStore>>;

const createContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  let unsubscribeEntityStore: (() => void) | null = null;

  return create<ContactsAppStore>()(
    subscribeWithSelector((set, get) => ({
      windowId,
      spaceId,
      isInitialized: false,
      isLoading: false,
      gmailConnected: null,
      contacts: [],
      selectedContactId: null,
      currentView: 'all',
      filter: { search: '', tier: null, tags: [], channel: null, warmth: null, staleOnly: false, identityType: null },
      isFormOpen: false,
      editingContactId: null,
      enrichingContactIds: new Set(),
      isDiscovering: false,
      isImporting: false,
      importResult: null,
      discoverResult: null,
      interactions: {},
      interactionsLoading: new Set(),
      syncLog: [],
      sourcesStatus: null,
      isLoadingSources: false,
      isSyncModalOpen: false,
      isSearchLoading: false,
      searchResults: null,
      searchSnippets: {},
      lastSearchQuery: '',

      initialize: async (winId: string, spcId: string) => {
        set({ isLoading: true, error: undefined });
        log.info('[ContactsApp] Initializing', { windowId: winId, spaceId: spcId });

        try {
          await get().checkEmailStatus();

          if (get().gmailConnected) {
            await get().loadContacts();
            // Fire-and-forget: ensure the hourly automation exists (catches users who
            // connected Gmail before this feature was shipped)
            get().ensureAutomation().catch(() => {});
          } else {
            set({ isInitialized: true, isLoading: false });
            return;
          }

          const useEntityStore = (await import('../../../stores/entityStore')).default;
          unsubscribeEntityStore = useEntityStore.subscribe((state: any, prevState: any) => {
            if (state.entities === prevState.entities) return;

            const currentContacts = get().contacts;

            const updatedContacts = state.entities
              .filter((e: any) => e.entityType === 'Contact' && !e.trashed)
              .map((e: any) => ({
                ...e,
                skeleton: { ...e.skeleton, tier: normalizeTier(e.skeleton?.tier) },
              }));

            const changed =
              updatedContacts.length !== currentContacts.length ||
              updatedContacts.some((c: ContactEntity) => {
                const existing = currentContacts.find((x) => x._id === c._id);
                return !existing || existing.updated_at !== c.updated_at || existing.name !== c.name;
              });

            if (changed) {
              log.info('[ContactsApp] Contacts updated from entity store');
              set({ contacts: updatedContacts });
            }
          });

          set({ isInitialized: true, isLoading: false });
        } catch (error) {
          log.error('[ContactsApp] Initialization failed', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize',
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      cleanup: () => {
        if (unsubscribeEntityStore) {
          unsubscribeEntityStore();
          unsubscribeEntityStore = null;
        }
        set({ contacts: [], isInitialized: false });
        storeMap.delete(`${windowId}-${spaceId}`);
      },

      checkEmailStatus: async () => {
        try {
          const result = await fetchService(`${API_BASE_URL}/contacts-app/email-status`);
          set({ gmailConnected: result?.connected === true });
        } catch (error) {
          log.error('[ContactsApp] checkEmailStatus failed', error);
          set({ gmailConnected: false });
        }
      },

      ensureAutomation: async () => {
        try {
          await fetchService(`${API_BASE_URL}/contacts-app/ensure-automation`, { method: 'POST' });
        } catch (error) {
          log.error('[ContactsApp] ensureAutomation failed', error);
        }
      },

      loadContacts: async () => {
        try {
          const raw: any[] = await fetchService(`${API_BASE_URL}/contacts-app`);
          // Normalize legacy tier values on load
          const contacts = (raw || []).map((c) => ({
            ...c,
            skeleton: { ...c.skeleton, tier: normalizeTier(c.skeleton?.tier) },
          }));
          set({ contacts });
        } catch (error) {
          log.error('[ContactsApp] Failed to load contacts', error);
          set({ error: 'Failed to load contacts' });
        }
      },

      setView: (view) => {
        set((state) => ({
          currentView: view,
          selectedContactId: null,
          filter: { ...state.filter, tier: null },
        }));
      },

      selectContact: (id) => {
        set({ selectedContactId: id });
      },

      setFilter: (filter) => {
        set((state) => ({ filter: { ...state.filter, ...filter } }));
      },

      createContact: async (data) => {
        try {
          const contact = await fetchService(`${API_BASE_URL}/contacts-app`, {
            method: 'POST',
            body: JSON.stringify(data),
          });
          const normalized = { ...contact, skeleton: { ...contact.skeleton, tier: normalizeTier(contact.skeleton?.tier) } };
          set((state) => ({ contacts: [...state.contacts, normalized] }));
          log.info('[ContactsApp] Contact created', contact._id);
          return normalized;
        } catch (error) {
          log.error('[ContactsApp] createContact failed', error);
          return null;
        }
      },

      updateContact: async (id, updates) => {
        try {
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
          });
          const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
          set((state) => ({
            contacts: state.contacts.map((c) => (c._id === id ? normalized : c)),
          }));
        } catch (error) {
          log.error('[ContactsApp] updateContact failed', error);
        }
      },

      deleteContact: async (id) => {
        try {
          await fetchService(`${API_BASE_URL}/contacts-app/${id}`, { method: 'DELETE' });
          set((state) => ({
            contacts: state.contacts.filter((c) => c._id !== id),
            selectedContactId: state.selectedContactId === id ? null : state.selectedContactId,
          }));
        } catch (error) {
          log.error('[ContactsApp] deleteContact failed', error);
        }
      },

      enrichContact: async (id) => {
        set((state) => {
          const next = new Set(state.enrichingContactIds);
          next.add(id);
          return { enrichingContactIds: next };
        });

        try {
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${id}/enrich`, {
            method: 'POST',
          });
          const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
          set((state) => ({
            contacts: state.contacts.map((c) => (c._id === id ? normalized : c)),
            enrichingContactIds: (() => {
              const next = new Set(state.enrichingContactIds);
              next.delete(id);
              return next;
            })(),
          }));
          log.info('[ContactsApp] Enriched contact', id);
        } catch (error) {
          log.error('[ContactsApp] enrichContact failed', error);
          set((state) => {
            const next = new Set(state.enrichingContactIds);
            next.delete(id);
            return { enrichingContactIds: next };
          });
        }
      },

      discoverContacts: async () => {
        set({ isDiscovering: true, discoverResult: null });
        try {
          const result = await fetchService(`${API_BASE_URL}/contacts-app/discover`, {
            method: 'POST',
          });
          await get().loadContacts();
          const discovered = result?.discovered ?? 0;
          set({ isDiscovering: false, discoverResult: { discovered } });
          setTimeout(() => set({ discoverResult: null }), 5000);
          log.info('[ContactsApp] Discovery complete', result);
          return result;
        } catch (error) {
          log.error('[ContactsApp] discoverContacts failed', error);
          set({ isDiscovering: false });
          return { discovered: 0, enriched: 0 };
        }
      },

      importFromCsv: async (file: File, source = 'csv') => {
        set({ isImporting: true, importResult: null });
        log.info('[ContactsApp] Starting CSV import', { fileName: file.name, source });

        try {
          const csvContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string ?? '');
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file, 'utf-8');
          });

          const result = await fetchService(`${API_BASE_URL}/tools/execute`, {
            method: 'POST',
            body: JSON.stringify({
              toolName: 'contacts_import_csv',
              params: { csvContent, source, defaultTier: 'regular' },
              spaceId: get().spaceId,
            }),
          });

          if (result.status === 'error') {
            throw new Error(result.error ?? 'Import failed');
          }

          const { created = 0, updated = 0, skipped = 0 } = result.data ?? result;
          log.info('[ContactsApp] CSV import complete', { created, updated, skipped });

          await get().loadContacts();
          set({ isImporting: false, importResult: { created, updated, skipped } });

          setTimeout(() => set({ importResult: null }), 5000);

          return { created, updated, skipped };
        } catch (error) {
          log.error('[ContactsApp] importFromCsv failed', error);
          set({ isImporting: false });
          throw error;
        }
      },

      openForm: (contactId) => {
        set({ isFormOpen: true, editingContactId: contactId || null });
      },

      closeForm: () => {
        set({ isFormOpen: false, editingContactId: null });
      },

      clearSyncLog: () => {
        set({ syncLog: [] });
      },

      fetchSourcesStatus: async () => {
        set({ isLoadingSources: true });
        try {
          const res = await fetchService(`${API_BASE_URL}/contacts-app/sources-status`, { method: 'GET' });
          set({ sourcesStatus: res, isLoadingSources: false });
        } catch (err) {
          log.error('[ContactsApp] Failed to fetch sources status', err);
          set({ isLoadingSources: false });
        }
      },

      getFilteredContacts: () => {
        const { contacts, currentView, filter } = get();
        let result = contacts;

        if (currentView !== 'all') {
          result = result.filter((c) => c.skeleton.tier === currentView);
        }

        if (filter.tier) {
          result = result.filter((c) => c.skeleton.tier === filter.tier);
        }

        if (filter.search) {
          const q = filter.search.toLowerCase();
          result = result.filter(
            (c) =>
              c.name.toLowerCase().includes(q) ||
              c.skeleton.email?.toLowerCase().includes(q) ||
              c.skeleton.company?.toLowerCase().includes(q)
          );
        }

        if (filter.tags.length > 0) {
          result = result.filter((c) =>
            filter.tags.every((tag) => c.skeleton.tags?.includes(tag))
          );
        }

        return result.sort((a, b) => {
          const aTier = TIER_ORDER.indexOf(a.skeleton.tier);
          const bTier = TIER_ORDER.indexOf(b.skeleton.tier);
          if (aTier !== bTier) return aTier - bTier;
          return a.name.localeCompare(b.name);
        });
      },
    }))
  );
};

const storeMap = new Map<string, ContactsStoreHook>();

export const getContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  const key = `${windowId}-${spaceId}`;
  if (!storeMap.has(key)) {
    storeMap.set(key, createContactsAppStore(windowId, spaceId));
  }
  return storeMap.get(key)!;
};

export const useContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  return getContactsAppStore(windowId, spaceId);
};
